// Initialize Sentry FIRST before any other imports
import './config/sentry';

import React from 'react';
import './i18n/i18n';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { Toaster } from 'react-hot-toast';
import { FeatureFlagProvider } from '@shared/flags/FeatureFlagProvider';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import App from './App';
import { queryClient, persistOptions } from './lib/offline/queryClient';
import { initOnlineManager } from './lib/offline/onlineManager';
import { registerOfflineMutationDefaults } from './lib/offline/mutationDefaults';
import { initOfflineCacheResetOnLogout } from './lib/offline/authCacheReset';
import { OnlineStatusProvider } from './contexts/OnlineStatusContext';
import './index.css';
import './theme/pro-theme.css';
import './theme/citizen-ionic.css';
import './theme/pro-ionic.css';

// Initialize Capacitor
import { Capacitor } from '@capacitor/core';
import { initKeyboardSafeArea } from '@shared/hooks/initKeyboardSafeArea';
import { installChunkReload } from './lib/chunkReload';

/* Ionic core CSS — required for IonApp to render properly */
import '@ionic/react/css/core.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Configure Ionic: use Material Design mode on web to avoid
   iOS-specific 300ms tap delay and swipe-back gesture interceptors.
   On a real iOS device via Capacitor, Ionic auto-detects 'ios' mode. */
import { setupIonicReact } from '@ionic/react';
setupIonicReact({
  mode: 'md',
});

// Toggle <html data-keyboard-up> while the soft keyboard is visible so
// `var(--cc-safe-area-bottom)` collapses to zero — see initKeyboardSafeArea
// and the CSS variable definition in index.css for the why.
initKeyboardSafeArea();

// #1453: after a deploy, a tab still on the previous build may lazy-import a
// chunk that no longer exists. Reload once so the tab picks up the new build
// instead of showing a blank panel. Web only: the native bundle ships its own
// chunks and never goes stale underneath a running app.
if (!Capacitor.isNativePlatform()) {
  installChunkReload();
}

// P2-12a: expose the delivery channel to CSS. The rule is that a treatment meant
// for the native shells must be gated on the PLATFORM, never on a breakpoint —
// every browser, phone browsers included, gets the responsive web UI (D-11).
// Breakpoint-gated theming is what P2-12b just deleted, so this attribute is how
// the remaining Ionic theme layers get scoped without reintroducing that mistake.
document.documentElement.setAttribute(
  'data-platform',
  Capacitor.isNativePlatform() ? 'native' : 'web'
);

// Drive React Query's online/offline state from Capacitor's Network plugin on
// native platforms (no-op on web). Must run before the QueryClient is used.
initOnlineManager();

// Register offline-capable mutation defaults (mutationFn + optimistic update +
// rollback) keyed by mutationKey, so mutations paused while offline can be
// replayed by resumePausedMutations() after a reload.
registerOfflineMutationDefaults(queryClient);

// Wipe the persisted offline cache on logout so cached workspace/notes data
// can't be read by the next user on a shared device.
initOfflineCacheResetOnLogout(queryClient);

// SplashScreen.hide() is deferred to App — hide only after the startup gate
// clears so App Review does not see a white WebView between splash and paint.

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={persistOptions}
        onSuccess={() => {
          // The query cache has just been restored from IndexedDB. Replay any
          // mutations that were queued while offline, then revalidate the
          // queries on the current screen so the UI catches up to server truth.
          queryClient
            .resumePausedMutations()
            .then(() => queryClient.invalidateQueries());
        }}
      >
        <OnlineStatusProvider>
          <FeatureFlagProvider>
            <BrowserRouter>
              <App />
              <OfflineBanner />
              <Toaster
                position="bottom-center"
                containerStyle={{
                  // Inset the toast container by safe-area on both ends so
                  // bottom toasts clear the iOS home indicator and top
                  // toasts (when used per-call) clear the notch.
                  top: 'env(safe-area-inset-top, 0px)',
                  bottom: 'env(safe-area-inset-bottom, 0px)',
                }}
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                }}
              />
            </BrowserRouter>
          </FeatureFlagProvider>
        </OnlineStatusProvider>
      </PersistQueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
